import { useEffect, useState } from 'react';

const defaultHeaderDefinition = ['Behandlung', 'Behandler', 'Datum & Uhrzeit', 'Persönliche Angaben', 'Bestätigung'];
const selectionColor = '#cbb58a';

const parseHeaderDefinition = (value: string | null) => {
  if (!value) {
    return defaultHeaderDefinition;
  }

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      const labels = parsed.map((item) => String(item).trim()).filter(Boolean);
      return labels.length > 0 ? labels : defaultHeaderDefinition;
    }
  } catch {
    const labels = value
      .trim()
      .replace(/^\[/, '')
      .replace(/\]$/, '')
      .split(',')
      .map((item) => item.trim().replace(/^['"]|['"]$/g, ''))
      .filter(Boolean);

    return labels.length > 0 ? labels : defaultHeaderDefinition;
  }

  return defaultHeaderDefinition;
};

const readHeaderDefinition = () => parseHeaderDefinition(localStorage.getItem('headerDefinition'));

const readHeaderPhase = () => {
  const phase = Number(localStorage.getItem('headerPhase')?.trim() || '1');
  return Number.isFinite(phase) && phase > 0 ? Math.floor(phase) : 1;
};

export function Header({ compact = false, steps, phase }: { compact?: boolean; steps?: readonly string[]; phase?: number }) {
  const [headerDefinition, setHeaderDefinition] = useState(readHeaderDefinition);
  const [headerPhase, setHeaderPhase] = useState(readHeaderPhase);
  const displayedSteps = steps ?? headerDefinition;
  const displayedPhase = phase ?? headerPhase;

  useEffect(() => {
    const syncHeader = () => {
      setHeaderDefinition(readHeaderDefinition());
      setHeaderPhase(readHeaderPhase());
    };

    window.addEventListener('storage', syncHeader);
    window.addEventListener('header-updated', syncHeader);
    return () => {
      window.removeEventListener('storage', syncHeader);
      window.removeEventListener('header-updated', syncHeader);
    };
  }, []);

  return (
    <header className={`bg-[#fbf9f5] px-6 pb-8 pt-10 text-slate-900${compact ? ' booking-progress' : ''}`}>
      <nav
        className={`mx-auto grid max-w-[1280px] items-start gap-4${compact ? ' booking-progress__steps' : ''}`}
        aria-label={compact ? 'Buchungsfortschritt' : undefined}
        style={{ gridTemplateColumns: `repeat(${displayedSteps.length}, minmax(0, 1fr))` }}
      >
        {displayedSteps.map((label, index) => {
          const step = index + 1;
          const isDone = step < displayedPhase;
          const isSelected = step <= displayedPhase;

          return (
            <div
              key={`${step}-${label}`}
              className={`flex min-w-0 flex-col items-center text-center${compact ? ' booking-progress__step' : ''}`}
              data-complete={compact && isDone ? 'true' : undefined}
              aria-current={compact && step === displayedPhase ? 'step' : undefined}
            >
              <span
                className={`flex h-14 w-14 items-center justify-center rounded-full border text-[22px] font-semibold shadow-[0_10px_22px_rgba(15,23,42,0.04)]${compact ? ' booking-progress__circle' : ''} ${
                  isSelected
                    ? 'border-[#cbb58a] bg-[#cbb58a] text-white'
                    : 'border-[#eef0f3] bg-white text-slate-300'
                }`}
                style={isSelected ? { backgroundColor: selectionColor, borderColor: selectionColor } : undefined}
              >
                {isDone ? '✓' : step}
              </span>
              <span
                className={`mt-5 max-w-full truncate text-[15px] font-semibold leading-5${compact ? ' booking-progress__label' : ''} ${
                  isSelected ? 'text-[#cbb58a]' : 'text-slate-300'
                }`}
              >
                {label}
              </span>
            </div>
          );
        })}
      </nav>
    </header>
  );
}
